import { currentToken, login, loginAdmin } from './auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public problems?: string[],
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, raw = false): Promise<T> {
  const token = await currentToken();
  if (!token) {
    await login();
    throw new ApiError(401, 'Требуется вход');
  }
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method, headers, body: payload });
  } catch {
    throw new ApiError(0, 'Нет связи с сервером', 'NETWORK');
  }
  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    // админка требует второй фактор или сессия админки истекла (AUTH-03)
    if (data.code === 'STEP_UP') await loginAdmin();
    else if (data.code === 'ADMIN_SESSION_EXPIRED') await loginAdmin(undefined, true);
    else await login();
    throw new ApiError(401, data.message ?? 'Сессия истекла', data.code);
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg = Array.isArray(data.message) ? data.message.join('; ') : data.message ?? `Ошибка ${res.status}`;
    throw new ApiError(res.status, msg, data.code, data.problems);
  }
  if (raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b ?? {}),
  put: <T>(p: string, b: unknown) => request<T>('PUT', p, b),
  patch: <T>(p: string, b: unknown) => request<T>('PATCH', p, b),
  del: (p: string) => request<void>('DELETE', p),
  upload: <T>(p: string, form: FormData) => request<T>('POST', p, form),
  blob: async (p: string) => (await request<Response>('GET', p, undefined, true)).blob(),
};

export async function download(path: string, filename: string) {
  const blob = await api.blob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
